import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { ReferingVetInterface } from '../models/refering-vet.interface';

@Injectable({
  providedIn: 'root'
})
export class ReferingVetService {
  ReferingVetsQuery = gql`query getReferingVets($clinicId: Long!) {
    referingVets(clinicId: $clinicId) {
      id
      lastName
      firstName
      postalCode
      city
      email
      clinicName
    }
  }`;

  ReferingVetsQueryVariables: { clinicId?: number } = {};

  constructor(private readonly apollo: Apollo) { }

  getReferingVets(clinicId: number): Observable<ReferingVetInterface[]> {
    this.ReferingVetsQueryVariables = { clinicId };
    return this.apollo.query<{ referingVets: ReferingVetInterface[] }>({
      query: this.ReferingVetsQuery,
      variables: this.ReferingVetsQueryVariables
    }).pipe(map(res => res.data.referingVets));
  }

  addReferingVet(referingVet: ReferingVetInterface): Observable<ReferingVetInterface | undefined> {
    return this.apollo.mutate<{ addReferingVet: ReferingVetInterface }>({
      mutation: gql`mutation addReferingVets($dto: ReferingVetAddDtoInput) {
        addReferingVet(dto: $dto) {
          id
          lastName
          firstName
          postalCode
          city
          email
          clinicName
        }
      }`,
      variables: {
        dto: {
          id : referingVet.id !== 0 ? referingVet.id : null,
          lastName : referingVet.lastName,
          firstName : referingVet.firstName,
          postalCode : referingVet.postalCode,
          city : referingVet.city,
          email : referingVet.email,
          clinicName : referingVet.clinicName,
        }
      },
      refetchQueries: [{
        query: this.ReferingVetsQuery,
        variables: this.ReferingVetsQueryVariables
      }],
      update: (store, { data }) => {
        // Read the data from our cache for this query.
        const referingVetQuery = store.readQuery<ReferingVetsQueryInterface>({ query: this.ReferingVetsQuery, variables: this.ReferingVetsQueryVariables }) ;
        if (referingVetQuery && data) {
          const referingVets = (referingVetQuery.referingVets).map(referingVets=> ({ ...referingVets }));
          if (!referingVets.map(k=> k.id).includes(data.addReferingVet.id)) {
            referingVets.push(data.addReferingVet);
            referingVets.sort((a, b) => (a.lastName + ' ' + a.firstName).localeCompare(b.lastName + ' ' + b.firstName));
            store.writeQuery({ query: this.ReferingVetsQuery, variables: this.ReferingVetsQueryVariables, data : { referingVets  }  });
          }
        }
      },
    }).pipe(map(res =>res.data ? res.data?.addReferingVet : undefined));
  }

  public deleteReferingVet(id: number): Observable<ReferingVetInterface | undefined> {
    return this.apollo.mutate<{ deleteReferingVet: ReferingVetInterface }>({
      mutation: gql`mutation deleteReferingVet($id: Long!) {
        deleteReferingVet(id: $id) {
          id
          lastName
          firstName
          postalCode
          city
          email
          clinicName
        }
      }`,
      variables: { id },
      refetchQueries: [{
        query: this.ReferingVetsQuery,
        variables: this.ReferingVetsQueryVariables
      }],
      update: (store, { data }) => {
        // Read the data from our cache for this query.
        const referingVetQuery = store.readQuery<ReferingVetsQueryInterface>({ query: this.ReferingVetsQuery, variables: this.ReferingVetsQueryVariables });
        if (referingVetQuery && data) {
          let referingVets = referingVetQuery.referingVets.map(referingVets=> ({ ...referingVets }));
          referingVets = referingVets.filter(a=>a.id !== data.deleteReferingVet.id);
          store.writeQuery({ query: this.ReferingVetsQuery, variables: this.ReferingVetsQueryVariables, data : { referingVets  }  });
        }
      },
    }).pipe(map(res =>res.data ? res.data.deleteReferingVet : undefined));
  }
}

export interface ReferingVetsQueryInterface {
  referingVets: ReferingVetInterface[];
}
